import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, updateDoc, doc, Timestamp, onSnapshot, limit, startAfter, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import { formatInTimeZone } from 'date-fns-tz';
import { useNavigate } from 'react-router-dom';

// Status badge color map
const statusColors = {
  scheduled: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  snoozed: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-gray-100 text-gray-600',
  error: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-200 text-gray-500',
  expired: 'bg-gray-200 text-gray-500',
};

const Reminders = () => {
  const { currentUser, authLoading } = useAuth();
  const [currentReminders, setCurrentReminders] = useState([]);
  const [archivedReminders, setArchivedReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [activeError, setActiveError] = useState(null);
  const [archivedError, setArchivedError] = useState(null);
  const [userTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedPage, setArchivedPage] = useState(1);
  const [hasMoreArchived, setHasMoreArchived] = useState(true);
  const [lastArchivedDoc, setLastArchivedDoc] = useState(null);
  const ARCHIVED_PER_PAGE = 10;
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[Reminders.js] useEffect fired', { authLoading, currentUser });
    if (authLoading || !currentUser) {
      setLoading(true);
      console.log('[Reminders.js] Waiting for auth or user', { authLoading, currentUser });
      return;
    }
    setLoading(true);
    setActiveError(null);

    const remindersRef = collection(db, 'users', currentUser.uid, 'reminderQueue');
    
    // Query for current reminders: sent and snoozed statuses
    const currentQ = query(
      remindersRef,
      where('reminderStatus', 'in', ['sent', 'snoozed']),
      where('archived', '==', false),
      orderBy('scheduledFor', 'desc')
    );

    console.log('[Reminders.js] Setting up onSnapshot for current reminders', { userId: currentUser.uid });
    const unsubCurrent = onSnapshot(currentQ, (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('[Reminders.js] onSnapshot success', { count: data.length, data });
      setCurrentReminders(data);
      setLoading(false);
      setActiveError(null);
    }, (err) => {
      console.error('[Reminders.js] onSnapshot error', err);
      setActiveError('Failed to load reminders.');
      setLoading(false);
    });

    return () => {
      console.log('[Reminders.js] Cleaning up onSnapshot');
      unsubCurrent();
    };
  }, [currentUser, authLoading]);

  // Load archived reminders with pagination
  const loadArchivedReminders = async (page = 1, append = false) => {
    if (!currentUser) return;
    console.log('[Reminders.js] loadArchivedReminders called', { page, append, currentUser });
    setArchivedLoading(true);
    setArchivedError(null);

    try {
      const remindersRef = collection(db, 'users', currentUser.uid, 'reminderQueue');
      let archivedQ = query(
        remindersRef,
        where('archived', '==', true),
        orderBy('archivedAt', 'desc'),
        limit(ARCHIVED_PER_PAGE)
      );

      // If loading more pages, start after the last document
      if (page > 1 && lastArchivedDoc) {
        archivedQ = query(
          remindersRef,
          where('archived', '==', true),
          orderBy('archivedAt', 'desc'),
          startAfter(lastArchivedDoc),
          limit(ARCHIVED_PER_PAGE)
        );
      }

      const snapshot = await getDocs(archivedQ);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('[Reminders.js] loadArchivedReminders success', { count: data.length, data });
      if (append) {
        setArchivedReminders(prev => [...prev, ...data]);
      } else {
        setArchivedReminders(data);
      }
      
      setLastArchivedDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMoreArchived(snapshot.docs.length === ARCHIVED_PER_PAGE);
      setArchivedPage(page);
    } catch (err) {
      console.error('[Reminders.js] loadArchivedReminders error', err);
      setArchivedError('Failed to load archived reminders.');
    } finally {
      setArchivedLoading(false);
    }
  };

  // Load more archived reminders
  const loadMoreArchived = () => {
    if (!hasMoreArchived || archivedLoading) return;
    loadArchivedReminders(archivedPage + 1, true);
  };

  // Toggle archived reminders visibility
  const toggleArchived = () => {
    if (!showArchived) {
      setShowArchived(true);
      loadArchivedReminders(1, false);
    } else {
      setShowArchived(false);
      setArchivedReminders([]);
      setArchivedPage(1);
      setLastArchivedDoc(null);
      setHasMoreArchived(true);
    }
  };

  // Action: Snooze reminder for 1 day
  const handleSnooze = async (reminderId) => {
    try {
      const reminder = currentReminders.find(r => r.id === reminderId);
      if (!reminder) return;
      const snoozeUntil = new Date(reminder.scheduledFor.toDate ? reminder.scheduledFor.toDate() : reminder.scheduledFor);
      snoozeUntil.setDate(snoozeUntil.getDate() + 1);
      await updateDoc(doc(db, 'users', currentUser.uid, 'reminderQueue', reminderId), {
        reminderStatus: 'snoozed',
        snoozedAt: Timestamp.now(),
        snoozedUntil: Timestamp.fromDate(snoozeUntil),
      });
    } catch (err) {
      setActiveError('Failed to snooze reminder.');
    }
  };

  // Action: Complete reminder
  const handleComplete = async (reminderId) => {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid, 'reminderQueue', reminderId), {
        reminderStatus: 'completed',
        completedAt: Timestamp.now(),
        completionReason: 'user_completed',
        archived: true,
        archivedAt: Timestamp.now(),
      });
    } catch (err) {
      setActiveError('Failed to complete reminder.');
    }
  };

  // Format date in user's timezone
  const formatDate = (ts) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return formatInTimeZone(date, userTimezone, 'MMM d, yyyy h:mm a zzz');
  };

  // Skeleton loader component
  const ReminderSkeleton = () => (
    <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 border border-gray-100 animate-pulse">
      <div className="flex-1 min-w-0">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-2/3 mb-1" />
        <div className="h-3 bg-gray-100 rounded w-1/2 mb-1" />
        <div className="h-3 bg-gray-100 rounded w-1/4" />
      </div>
      <div className="flex flex-row sm:flex-col gap-2 mt-2 sm:mt-0">
        <div className="h-7 w-16 bg-gray-200 rounded" />
        <div className="h-7 w-16 bg-gray-200 rounded" />
      </div>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4 text-center">My Reminders</h1>
      {console.log('[Reminders.js] Render', { authLoading, loading, currentUser, activeError, currentReminders })}
      {(authLoading || loading || !currentUser) && (
        <div className="space-y-4">
          {[1,2,3].map(i => <ReminderSkeleton key={i} />)}
        </div>
      )}
      {!authLoading && !loading && currentUser && (
        <>
          {activeError && currentReminders.length === 0 && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-center">{activeError}</div>
          )}
          {currentReminders.length === 0 && (
            <div className="text-center text-gray-500 mt-8">No active reminders. Enjoy your day!</div>
          )}
          <div className="space-y-4">
            {currentReminders.map(reminder => {
              console.log('[Reminders.js] Reminder:', {
                id: reminder.id,
                discipleName: reminder.discipleName,
                frequency_at_creation: reminder.frequency_at_creation,
                reminderStatus: reminder.reminderStatus,
                scheduledFor: reminder.scheduledFor,
                snoozedUntil: reminder.snoozedUntil,
              });
              return (
                <div
                  key={reminder.id}
                  className={`bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 border border-gray-100
                    ${reminder.reminderStatus === 'snoozed' ? 'opacity-60 bg-yellow-50' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-lg truncate">{reminder.discipleName || 'Disciple'}</span>
                      {reminder.reminderStatus === 'snoozed' && (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-yellow-200 text-yellow-900">Snoozed</span>
                      )}
                    </div>
                    <div className="text-gray-700 mb-1 truncate">{reminder.message}</div>
                    <div className="text-xs text-gray-500">Scheduled: {formatDate(reminder.scheduledFor)}</div>
                    {reminder.snoozedUntil && (
                      <div className="text-xs text-yellow-700 mt-1 font-semibold">
                        Snoozed until: {formatDate(reminder.snoozedUntil)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 mt-2 sm:mt-0 min-w-[120px] items-end">
                    {/* Main action: Send a Message */}
                    <button
                      onClick={() => navigate(`/suggested-verses/${reminder.discipleId}`)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold text-sm w-full transition"
                    >
                      Send a Message
                    </button>
                    {/* Snooze button (not shown if already snoozed or completed) */}
                    {reminder.reminderStatus !== 'snoozed' && reminder.reminderStatus !== 'completed' && (
                      <button
                        onClick={() => handleSnooze(reminder.id)}
                        className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded font-medium text-xs hover:bg-yellow-200 transition w-full"
                      >
                        Snooze
                      </button>
                    )}
                    {/* Mark Complete link (not shown if already completed) */}
                    {reminder.reminderStatus !== 'completed' && (
                      <button
                        onClick={() => handleComplete(reminder.id)}
                        className="text-gray-400 hover:text-gray-600 text-xs underline bg-transparent p-0 mt-1 w-auto"
                        style={{ boxShadow: 'none', border: 'none' }}
                      >
                        Mark Complete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Archived Reminders Toggle and Section */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={toggleArchived}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition"
        >
          {showArchived ? 'Hide Archived Reminders' : 'Show Archived Reminders'}
        </button>
      </div>

      {showArchived && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-4 text-center">Archived Reminders</h2>
          
          {archivedError && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-center">{archivedError}</div>
          )}
          
          {archivedLoading && archivedReminders.length === 0 && (
            <div className="flex justify-center items-center h-32">
              <span className="text-gray-500">Loading archived reminders...</span>
            </div>
          )}
          
          {!archivedLoading && archivedReminders.length === 0 && (
            <div className="text-center text-gray-500">No archived reminders found.</div>
          )}
          
          <div className="space-y-4">
            {archivedReminders.map(reminder => (
              <div
                key={reminder.id}
                className="bg-gray-50 rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 border border-gray-200"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-lg truncate">{reminder.discipleName || 'Disciple'}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${statusColors[reminder.reminderStatus] || 'bg-gray-100 text-gray-600'}`}>{reminder.reminderStatus}</span>
                  </div>
                  <div className="text-gray-700 mb-1 truncate">{reminder.message}</div>
                  <div className="text-xs text-gray-500">Scheduled: {formatDate(reminder.scheduledFor)}</div>
                  {reminder.completedAt && (
                    <div className="text-xs text-green-700 mt-1">Completed: {formatDate(reminder.completedAt)}</div>
                  )}
                  {reminder.archivedAt && (
                    <div className="text-xs text-gray-500 mt-1">Archived: {formatDate(reminder.archivedAt)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination for archived reminders */}
          {hasMoreArchived && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={loadMoreArchived}
                disabled={archivedLoading}
                className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-medium hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {archivedLoading ? 'Loading...' : 'Load More Archived'}
              </button>
            </div>
          )}
          
          {archivedReminders.length > 0 && (
            <div className="mt-4 text-center text-sm text-gray-500">
              Showing {archivedReminders.length} archived reminders
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reminders;
